import * as R from 'ramda';
import { apiSubmitWaiver } from '../../../core/redux/api/endpoints';
import { ApiReducer, makeApiAction, makeApiReducer, RsaaTypes } from '../../../core/redux/api/reduxStandardApiAction';
import { not, assoc } from '../../../language/module';
import { hasVersion } from './module';
import { WaiverSignature } from './waiverSignature';
import { ACTION_TYPE } from '../../../core/redux/actionType';
import { addError, addLoading, getLoading, removeLoadingAndError } from '../../../core/redux/api/module';
import { hasTrippedBreaker, updateBreaker } from '../../../core/redux/api/circuitBreaker/module';
import { GuestDetails } from '../../guestDetails';
import { Waiver } from './waiver';
import { updateGuest } from '../../module';
import { getResource } from '../../../language/resources/getResource';


const KEY_ACTION = ACTION_TYPE.SUBMIT_WAIVER_REQUEST;
const ACTIONS    = [
  ACTION_TYPE.SUBMIT_WAIVER_REQUEST,
  ACTION_TYPE.SUBMIT_WAIVER_RESPONSE,
  ACTION_TYPE.SUBMIT_WAIVER_ERROR,
];


/**
 * Constructs a waiver Redux Action for posting waiver RFID signature to api
 */
export const submitWaiverAction = (guest: GuestDetails, signer: WaiverSignature) => makeApiAction(
  {
    method:   'POST',
    endpoint: apiSubmitWaiver(guest.masterGuestId),
    headers:  state => ({
      'content-type':  'application/json',
      'Authorization': `Bearer ${state.auth.bearerToken}`,
    }),
    body:  state =>   JSON.stringify(
      {
        userId:          state.userState.userId,
        signerFirstName: signer.firstName,
        signerLastName:  signer.lastName,
        signerDob:       new Date(signer.dateOfBirth).toISOString(),
        waiverVersion:   signer.version,
        resortAbbrev:    state.selectedResort.resortAbbreviation
      },
    ),
    types:    ACTIONS.map(action => ({
      type: action,
      meta: { guest, signer },
    })) as RsaaTypes,
    bailout:  state => !!getLoading(state, guest.masterGuestId, KEY_ACTION) || hasTrippedBreaker(state, KEY_ACTION),
  },
);


const reducer: ApiReducer<undefined> = (state, action, error, isLoading, payload) => {
  const metaGuest     = action.meta.guest;
  const metaSignature = action.meta.signer as WaiverSignature;
  const guestId       = metaGuest.masterGuestId;

  if (isLoading) {
    return addLoading(state, guestId, KEY_ACTION);
  }

  const withBreaker = updateBreaker(state, KEY_ACTION, !!error);

  if (error) {
    return addError(
      withBreaker,
      guestId,
      KEY_ACTION,
      getResource(state.locale, 'WAIVER_UNKNOWN_ERROR'),
      { guest: metaGuest },
    );
  }

  const noLoadingOrError = removeLoadingAndError(withBreaker, guestId, KEY_ACTION);

  const newWaiver: Waiver = {
    waiverType:            +metaSignature.version,
    version:               metaSignature.version,
    waiverComments:        getResource(state.locale, 'WAIVER_GENERIC_COMMENT'),
    signedByMasterGuestId: '',
    signedDate:            new Date().toISOString(),
    expirationDate:        new Date('1/1/3000').toISOString(),
    status:                1,
  };

  return updateGuest(
    noLoadingOrError,
    guestId,
    guest => assoc(
      'waivers',
      R.concat(
        [ newWaiver ],
        guest.waivers.filter(not(hasVersion(metaSignature.version))),
      ),
      guest,
    ),
  );
};


export const submitWaiverReducer = makeApiReducer(ACTIONS, KEY_ACTION, reducer);

